import { Map as MapLibreMap, ScaleControl, MapMouseEvent } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { mapEngine } from './MapEngineService';
import { mapStyleService } from './MapStyleService';
import type { MapViewerProps, BoundingBox } from './types';
import { eventBus, CoreEvents } from '../event-bus';
import type { MapFeatureSelectedPayload } from '../event-bus/types';
import { MapControls } from './MapControls';
import { getActiveOpeningPerfSessionId, markOpeningPerfEvent } from '../utils';
import { getActiveMapToolSnapshot } from '../map-tools/MapToolsContext';
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * Calcula el zoom inicial óptimo para un bounding box
 * Esto evita que el mapa arranque con zoom global y luego haga fitBounds
 *
 * @param bounds - Bounding box del proyecto
 * @param containerWidth - Ancho del contenedor en píxeles
 * @param containerHeight - Alto del contenedor en píxeles
 * @returns Nivel de zoom óptimo (entre 1 y 16)
 */
function calculateInitialZoom(
  bounds: BoundingBox,
  containerWidth: number,
  containerHeight: number
): number {
  const ZOOM_MAX = 16;
  const ZOOM_MIN = 1;

  // Descontar el padding del panel lateral y barras (igual que INITIAL_VIEW_PADDING)
  // para que el zoom inicial sea coherente con el fitBounds posterior.
  const effectiveWidth  = Math.max(containerWidth  - 340 - 40,  100); // left:340 right:40
  const effectiveHeight = Math.max(containerHeight - 80  - 60,  100); // top:80 bottom:60

  const lngSpan = bounds.maxLng - bounds.minLng;
  const latSpan = bounds.maxLat - bounds.minLat;

  // Zoom que hace que el span de longitud quepa en el contenedor efectivo.
  // A zoom 0 el mundo (360°) ocupa 256px → a zoom z ocupa 256·2^z px.
  const zoomLng = Math.log2(effectiveWidth  * 360 / lngSpan / 256);

  // Ídem para latitud. Para spans pequeños (<10°) la aproximación equirectangular
  // es suficientemente precisa; evita usar la latitud central (que daba zoom≈1).
  const zoomLat = Math.log2(effectiveHeight * 360 / latSpan / 256);

  return Math.max(Math.min(zoomLng, zoomLat, ZOOM_MAX) - 0.3, ZOOM_MIN);
}

const INITIAL_VIEW_PADDING = {
  top: 80,
  bottom: 60,
  left: 340,
  right: 40,
} as const;

function getBoundsKey(bounds?: BoundingBox): string | null {
  if (!bounds) {
    return null;
  }

  return [
    bounds.minLng,
    bounds.minLat,
    bounds.maxLng,
    bounds.maxLat,
  ].join(':');
}

function applyInitialView(
  map: MapLibreMap,
  bounds: BoundingBox,
  restrictNavigation: boolean
): void {
  const lngLatBounds: [[number, number], [number, number]] = [
    [bounds.minLng, bounds.minLat],
    [bounds.maxLng, bounds.maxLat],
  ];

  map.fitBounds(lngLatBounds, {
    padding: INITIAL_VIEW_PADDING,
    duration: 0,
    animate: false,
  });

  map.setMaxBounds(restrictNavigation ? lngLatBounds : null);
}

/**
 * MapViewer - Componente React para visualización de mapas con MapLibre GL
 *
 * @remarks
 * Componente base para visualización de mapas en WESTEER.
 * Integra MapLibre GL JS con React y proporciona controles básicos.
 *
 * @example
 * ```tsx
 * import { MapViewer } from '@westeer/core-frontend/map-engine';
 *
 * function MyMap() {
 *   const handleMapLoad = (map) => {
 *     console.log('Mapa cargado:', map);
 *   };
 *
 *   return (
 *     <MapViewer
 *       initialBounds={{
 *         minLng: -170,
 *         minLat: -60,
 *         maxLng: -30,
 *         maxLat: 75
 *       }}
 *       onMapLoad={handleMapLoad}
 *       onMapClick={(event) => {
 *         console.log('Click en:', event.lngLat);
 *       }}
 *     />
 *   );
 * }
 * ```
 */
export function MapViewer({
  initialBounds,
  style = mapStyleService.getDefaultStyle(),
  height = '100vh',
  width = '100%',
  onMapShellReady,
  onMapStyleReady,
  onMapLoad,
  onMapClick,
  showControls = true,
  showScale = true,
  restrictNavigation = false,
  detectBaseMapFeatures = false, // FEATURE FUTURA: importar features de OSM
}: MapViewerProps): JSX.Element {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const revealedRef = useRef(false);
  const styleReadyRef = useRef(false); // Fase 2D.2: guard para styledata
  const appliedBoundsKeyRef = useRef<string | null>(null);
  const appliedRestrictNavigationRef = useRef<boolean>(restrictNavigation);
  const [mapInstance, setMapInstance] = useState<MapLibreMap | null>(null);
  const [mapReady, setMapReady] = useState(false); // Control de visibilidad del mapa

  useEffect(() => {
    if (!mapContainerRef.current) return;

    const openingPerfSessionId = getActiveOpeningPerfSessionId();

    // ✨ CAMBIO: Calcular zoom inicial óptimo para evitar vista global innecesaria
    const containerWidth = mapContainerRef.current.clientWidth || 800;
    const containerHeight = mapContainerRef.current.clientHeight || 600;

    markOpeningPerfEvent(openingPerfSessionId, 'map:create-start');

    // Inicializar mapa
    const map = new MapLibreMap({
      container: mapContainerRef.current,
      style,
      center: initialBounds
        ? [
            (initialBounds.minLng + initialBounds.maxLng) / 2,
            (initialBounds.minLat + initialBounds.maxLat) / 2,
          ]
        : [-95, 0], // Centro de América por defecto
      // ✨ ANTES: zoom: 3 (vista global → fitBounds animado)
      // ✨ AHORA: zoom calculado para mostrar directamente el área del proyecto
      zoom: initialBounds ? calculateInitialZoom(initialBounds, containerWidth, containerHeight) : 3,
      preserveDrawingBuffer: true, // Permite capturar el canvas para PDFs/screenshots
    });

    mapRef.current = map;
    // Registrar el mapa en MapEngineService INMEDIATAMENTE — antes del 'load' event.
    // Esto permite que el SPIKE empiece a detectar el mapa y esperar isStyleLoaded()
    // desde el primer poll, en vez de esperar a que el 'load' event dispare (~2-10s).
    mapEngine.setMap(map);
    setMapInstance(map); // Actualizar estado para MapControls
    markOpeningPerfEvent(openingPerfSessionId, 'map:create-end');

    // Fase 2D.2: mapShellReady — map object exists, MapEngine registered.
    // Dispara inmediatamente, sin esperar tiles ni style. Permite desbloquear UI básica.
    onMapShellReady?.();

    // Agregar escala
    if (showScale) {
      map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');
    }

    // Fase 2D.2: mapStyleReady — primer styledata:style event.
    // Indica que el style JSON está parseado. Revela el canvas y dispara onMapStyleReady.
    // Usamos listener regular (no .once) para filtrar solo dataType==='style'.
    const onFirstStyleData = (e: { dataType?: string }): void => {
      if (e.dataType !== 'style') return;
      map.off('styledata', onFirstStyleData);
      if (!styleReadyRef.current) {
        styleReadyRef.current = true;
        markOpeningPerfEvent(openingPerfSessionId, 'map:style-ready');
        onMapStyleReady?.();
        // Revelar canvas en styledata:style — no esperar tiles completos.
        // El NetworkLoadingScreen todavía puede estar visible (fade en progreso).
        if (!revealedRef.current) {
          setMapReady(true);
        }
      }
    };
    map.on('styledata', onFirstStyleData);

    // Evento de carga del mapa
    map.on('load', () => {

      if (initialBounds) {
        applyInitialView(map, initialBounds, restrictNavigation);
        appliedBoundsKeyRef.current = getBoundsKey(initialBounds);
        appliedRestrictNavigationRef.current = restrictNavigation;
      }

      // Callback de carga (mapVisualReady en MapLayout)
      onMapLoad?.(map);

      // map:revealed — tiles iniciales cargados, canvas completamente pintado.
      // setMapReady es fallback si styledata no disparó antes.
      if (!revealedRef.current) {
        revealedRef.current = true;
        markOpeningPerfEvent(openingPerfSessionId, 'map:revealed');
        setMapReady(true);
      } else {
        markOpeningPerfEvent(openingPerfSessionId, 'map:revealed');
      }
    });

    // Evento de click - detección de features y emisión de eventos
    const handleMapClick = (e: MapMouseEvent): void => {
      // Ejecutar callback original si existe
      onMapClick?.(e);

      // Guard mínimo: durante medición evitar la selección global del mapa.
      // La herramienta de medición sigue recibiendo su propio click via map.on('click').
      if (getActiveMapToolSnapshot() === 'measurement') {
        return;
      }

      // Detectar features en el punto clickeado
      //
      // FEATURE FUTURA (detectBaseMapFeatures=true):
      // Permitirá click en plaza/edificio OSM → ver propiedades → importar a WESTEER
      //
      // Por ahora filtramos solo capas de WESTEER para evitar conflictos
      // Capas candidatas de WESTEER. Se filtran por existencia real en el estilo
      // para evitar el error de MapLibre al consultar capas no cargadas aún.
      const WESTEER_LAYERS = [
        // Módulo Environmental
        'env-trees',
        'env-parks-fill',
        'env-parks-line',
        'env-hotspots-fill',
        'env-hotspots-line',
        'env-interventions',
        // TODO: Agregar capas de otros módulos aquí
      ];

      const queryOptions = detectBaseMapFeatures
        ? {} // Sin filtro: detectar TODAS las features (incluyendo OSM)
        : (() => {
            const presentLayers = WESTEER_LAYERS.filter((id) => !!map.getLayer(id));
            // Si ninguna capa WESTEER está cargada, no hay nada que detectar
            return presentLayers.length > 0 ? { layers: presentLayers } : null;
          })();

      // queryOptions === null significa que no hay capas WESTEER cargadas: no detectar nada
      if (!queryOptions) return;

      const features = map.queryRenderedFeatures(e.point, queryOptions);

      if (features && features.length > 0) {
        // Feature seleccionado (tomar el primero, que es el más visible/top)
        const feature = features[0];
        const layerId = feature.layer?.id;

        console.log('[MapViewer] Feature seleccionado:', feature, 'Layer:', layerId);

        // Resaltar feature en el mapa
        mapEngine.highlightFeature(feature, layerId);

        // Emitir evento de feature seleccionado
        const payload: MapFeatureSelectedPayload = {
          feature,
          layerId,
          lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
        };

        eventBus.emit(CoreEvents.MAP_FEATURE_SELECTED, payload);
      } else {
        // No hay features - deseleccionar
        console.log('[MapViewer] Clic en mapa sin features - deseleccionando');

        // Limpiar highlight
        mapEngine.clearHighlight();

        eventBus.emit(CoreEvents.MAP_FEATURE_DESELECTED, {});
      }
    };

    map.on('click', handleMapClick);

    // Cleanup
    return (): void => {
      map.off('styledata', onFirstStyleData);
      map.off('click', handleMapClick);
      map.remove();
      mapRef.current = null;
      revealedRef.current = false;
      styleReadyRef.current = false;
      appliedBoundsKeyRef.current = null;
      appliedRestrictNavigationRef.current = restrictNavigation;
      setMapInstance(null);
      // Resetear visibilidad: evita que el segundo mount (StrictMode) arranque
      // con opacity:1 antes de que el nuevo mapa haya renderizado su vista inicial.
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar en mount/unmount

  /**
   * Reaplica bounds sin ocultar el canvas cuando cambia el proyecto.
   */
  useEffect(() => {
    const map = mapRef.current;
    const nextBoundsKey = getBoundsKey(initialBounds);

    if (!map || !initialBounds || !map.loaded()) {
      return;
    }

    if (
      nextBoundsKey === appliedBoundsKeyRef.current &&
      restrictNavigation === appliedRestrictNavigationRef.current
    ) {
      return;
    }

    applyInitialView(map, initialBounds, restrictNavigation);
    appliedBoundsKeyRef.current = nextBoundsKey;
    appliedRestrictNavigationRef.current = restrictNavigation;
  }, [initialBounds, restrictNavigation]);

  return (
    <>
      <div
        ref={mapContainerRef}
        style={{
          width,
          height,
          position: 'relative',
          opacity: mapReady ? 1 : 0, // Ocultar hasta estado estable
          transition: 'opacity 0.3s ease-in', // Fade in suave
        }}
        data-testid="map-container"
      />
      {showControls && <MapControls map={mapInstance} />}
    </>
  );
}
